/**
 * Der Scan-Einstieg (KONZEPT.md §8).
 *
 * Scannen ist der Haupteingang ins System. Diese Seite nimmt einen Code und
 * schickt weiter: Lagerort-QR zur Lagerortmaske, Artikelcode zum
 * Aktionsmenü.
 *
 * Und den dritten Fall, den §8.3 verlangt: **Ein unbekannter Code führt nie in
 * eine Sackgasse.** Im Einsatz ist eine Fehlermeldung ohne Ausweg das
 * Schlimmste.
 *
 * Umschalter **Einzel | Mehrfach** (ADR 0013 §5): Einzel öffnet die Seite des
 * Gescannten, Mehrfach sammelt in eine Liste (`Mehrfach.tsx`). Welcher zuletzt
 * gewählt war, merkt sich das Gerät.
 */

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { einstellungenSetzen, useEinstellungen } from "../api/einstellungen.js";
import { codeAufloesen } from "../scan/aufloesen.js";
import { Scanner, rueckmeldungGeben } from "../scan/Scanner.jsx";
import { Rueckmeldung, Wahl } from "../ui/bausteine.jsx";
import { GeraetAnlegen } from "../ui/GeraetAnlegen.jsx";
import { OhneCode } from "../ui/OhneCode.jsx";
import {
  Nachzuerfassen,
  UnbekannterCodeText,
  unbekanntErfassen,
  unbekanntKopf,
  type Unbekannt,
} from "../ui/UnbekannterCode.jsx";
import { Mehrfach } from "./Mehrfach.jsx";

/**
 * Die Sammelliste ueberlebt das Wechseln der Ansicht (Rueckmeldungen 1.1): Wer
 * zwischendurch ein Geraet ansieht, findet sie beim Zurueck wieder. Im Speicher
 * des Tabs, nicht dauerhaft.
 */
const LISTE_SCHLUESSEL = "thw-sammelliste";

function listeLesen(): readonly string[] {
  try {
    const roh = globalThis.sessionStorage.getItem(LISTE_SCHLUESSEL);
    const wert: unknown = roh === null ? [] : JSON.parse(roh);
    return Array.isArray(wert) ? wert.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

export function ScanEinstieg() {
  const { scanmodus } = useEinstellungen();
  // Die Sammelliste lebt hier, damit sie ein Umschalten auf Einzel übersteht.
  const [liste, setListe] = useState<readonly string[]>(listeLesen);
  useEffect(() => {
    try {
      globalThis.sessionStorage.setItem(LISTE_SCHLUESSEL, JSON.stringify(liste));
    } catch {
      // Ohne Speicher lebt die Liste eben nur, solange die Seite offen ist.
    }
  }, [liste]);

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-xl font-bold">Scannen</h1>
      <Wahl
        beschriftung="Wie scannen?"
        wert={scanmodus}
        moeglichkeiten={[
          { wert: "einzel", text: "Einzel" },
          {
            wert: "mehrfach",
            text: liste.length === 0 ? "Mehrfach" : `Mehrfach · ${String(liste.length)}`,
          },
        ]}
        aendern={(neu) => {
          einstellungenSetzen({ scanmodus: neu });
        }}
      />
      {scanmodus === "mehrfach" ? <Mehrfach liste={liste} setListe={setListe} /> : <Einzel />}
    </div>
  );
}
/** Ein Scan öffnet die Seite des Gescannten: Artikel, Packstück, Lagerort. */
function Einzel() {
  const gehZu = useNavigate();
  const [unbekannt, setUnbekannt] = useState<Unbekannt | null>(null);
  const [anlegen, setAnlegen] = useState<Unbekannt | null>(null);

  const aufCode = useCallback(
    async (code: string) => {
      const ergebnis = await codeAufloesen(code);

      if (ergebnis.art === "lagerort") {
        rueckmeldungGeben(true);
        void gehZu(`/lagerort/${ergebnis.lagerort.id}`);
        return;
      }
      if (ergebnis.art === "artikel") {
        rueckmeldungGeben(true);
        void gehZu(`/artikel/${ergebnis.artikel.id}`);
        return;
      }

      rueckmeldungGeben(false);
      setUnbekannt(await unbekanntErfassen(ergebnis.code, ergebnis.vorschlag, null));
      // Die Meldung steht oben. Wer unten in der Suche war, soll sie sehen.
      globalThis.scrollTo({ top: 0, behavior: "smooth" });
    },
    [gehZu],
  );

  if (anlegen !== null) {
    return (
      <GeraetAnlegen
        code={anlegen.code}
        vorschlag={anlegen.vorschlag}
        ort_id={anlegen.ort_id}
        aufAbbruch={() => {
          setAnlegen(null);
        }}
        aufVorhanden={(artikel) => {
          void gehZu(`/artikel/${artikel.id}`);
        }}
        aufFertig={(artikel) => {
          void gehZu(`/artikel/${artikel.id}`);
        }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {unbekannt !== null && (
        <Rueckmeldung
          art="warnung"
          kopf={unbekanntKopf(unbekannt)}
          zusatz={
            <UnbekannterCodeText
              unbekannt={unbekannt}
              aufAnlegen={() => {
                setAnlegen(unbekannt);
                setUnbekannt(null);
              }}
              aufVergessen={() => {
                setUnbekannt(null);
              }}
            />
          }
          aktion={{
            text: "Weiter scannen",
            tun: () => {
              setUnbekannt(null);
            },
          }}
        />
      )}

      <Nachzuerfassen aufAnlegen={setAnlegen} />

      <Scanner aufCode={(code) => void aufCode(code)} />

      <OhneCode
        aufArtikel={(id) => {
          void gehZu(`/artikel/${id}`);
        }}
        aufNichtDabei={(eingabe) => void aufCode(eingabe)}
      />
    </div>
  );
}
